<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    :embedded="embedded">
    <div class="row no-gutters h-100">
      <div class="col-2 p-2">
        <AnalyticsDashboardValueRow>
          <div class="col">
            <ReportFilterPopover
              headerTitle="Monthly Order Collection Filter"
              :showApplyButton="true"
              :showResetButton="true"
              :appendToBody="true"
              alignment="left"
              placement="bottom-end"
              @apply="applyOrderCollectionFilter"
              @reset="resetOrderCollectionFilter">
              <template v-slot:popover>
                <ReportDateRangeSelect 
                  :startDate="editingOrderCollectionFilter.startDate"
                  :endDate="editingOrderCollectionFilter.endDate"
                  @updateStartDate="editingOrderCollectionFilter.startDate = $event"
                  @updateEndDate="editingOrderCollectionFilter.endDate = $event"
                />
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedOrderCollectionFilter.startDate | formatDate}} - {{appliedOrderCollectionFilter.endDate | formatDate}}
                </div>                
              </template>
            </ReportFilterPopover>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Total Order Count" highlight>
              {{formatInteger(orderSummaryData.orderTotalCount, false, true)}}
              <template v-slot:info>Orders collected during the date range</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
      </div>
      <div class="col-10">
        <ChartBar
          title="Monthly Order Collection"
          :series="orderMonthlyCollectionData.series"
          :xAxis="orderMonthlyCollectionData.xAxis"
          :yAxis="orderMonthlyCollectionData.yAxis"
          :legend="orderMonthlyCollectionData.legend"
          :options="orderMonthlyCollectionData.options"
          :popupEnabled="true"
          :exportEnabled="true"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import ChartBar from '@/components/charts/chartBar'

export default {
  name: 'OrderDIFOTMonthlyCollectionWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartBar,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    filter: Object,
    contactId: Number,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean
  },
  data() {
    return {
      orderSummaryData: {
        orderTotalCount: 0,
      },
      orderMonthlyCollectionData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultOrderCollectionFilter: {    
        startDate: moment().local().subtract(6, 'months').startOf('month').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString()
      },
      editingOrderCollectionFilter: {},
      appliedOrderCollectionFilter: {},
    }
  },
  computed: {
    hasContactFilter() {
      return this.contactId != null
    }
  },
  mounted() {
    this.editingOrderCollectionFilter = _.clone(this.defaultOrderCollectionFilter)
    this.appliedOrderCollectionFilter = _.clone(this.defaultOrderCollectionFilter)

    this.requestChartData()

    this.subscribeEndpoint('/orders', () => {
      this.requestChartData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/orders')
  },
  watch: {
    contactId: function() {
      this.requestChartData()
    },
    filter: function() {
      this.requestChartData()
    }
  },
  methods: {
    applyOrderCollectionFilter() {
      this.appliedOrderCollectionFilter = _.clone(this.editingOrderCollectionFilter)

      this.requestChartData()
    },
    resetOrderCollectionFilter() {
      this.editingOrderCollectionFilter = _.clone(this.defaultOrderCollectionFilter)
      this.appliedOrderCollectionFilter = _.clone(this.defaultOrderCollectionFilter)

      this.requestChartData()
    },
    requestChartData() {
      var orderCollectionFilter = {
        type: 'monthly',
        startDate: this.appliedOrderCollectionFilter.startDate,
        endDate: this.appliedOrderCollectionFilter.endDate,
        contactId: this.contactId,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null
      }

      const widgetComp = this
      this.$apiService.analyticsService.getOrderDIFOTCollectionDelayChartData(orderCollectionFilter)
      .then(response => {
        let chartData = response.data

        // Summarise data
        this.orderSummaryData.orderTotalCount = chartData.reduce((curValue, dayInfo) => curValue + dayInfo.totalOrderCount, 0)

        let seriesList = []

        if(chartData.length > 0) {
          var seriesData = chartData.map(chartEntry => {

            return {
              avgDespatchDurationDays: chartEntry.avgDespatchDurationDays,
              date: moment(chartEntry.date).format('MMM YYYY')
            }
          })

          var targetDelay = parseFloat(chartData[0].operationalReportingTargetCollectionDays || 0)

          seriesList.push({
            name: `Avg. Collection Delay`,
            type: 'line',
            data: seriesData.map(chartEntry => chartEntry.avgDespatchDurationDays),
            tooltip: {
              valueFormatter: (value) => {
                return widgetComp.formatDecimal(value, 1) + ' days';
              }
            },
            markLine: {
              name: 'Target',
              data: [{ yAxis: targetDelay }],
              lineStyle: {
                type: 'dashed',
                color: 'red'
              },
              label: {
                show: true,
                formatter: 'Target: ' + this.formatDecimal(targetDelay, 1) + ' days'
              }
            },
          })
        }

        this.orderMonthlyCollectionData.legend = {
          data: ['Avg. Collection Delay']
        }
        this.orderMonthlyCollectionData.xAxis = {
          type: 'category',
          data: seriesData.map(chartEntry => chartEntry.date)
        }
        this.orderMonthlyCollectionData.yAxis = [
          {
            type: 'value',
            axisLabel: {
              formatter: (value) => {
                return this.formatDecimal(value, 1)
              }
            }
          }
        ]
        this.orderMonthlyCollectionData.series = seriesList
      })
    },
  }
}
</script>